import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, Chip, Grid, Popover, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IconButton from "@mui/material/IconButton";
import { LumaConceptType, useVideo } from "../../../../../context/juno/VideoContext";
import { Videos } from "../../../../../assets";
import { RowBox, RowCenteredBox } from "../../../../../utils/styledBox";
import VideocamIcon from "@mui/icons-material/Videocam";
import ScrollableBox from "../../../../../components/common/ScrollableBox";

interface ConceptMapping {
  value: LumaConceptType;
  label: string;
  video: string;
}

const LumaConceptsControl: React.FC = () => {
  const { t } = useTranslation();
  const { lumaConcepts, setLumaConcepts } = useVideo();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConceptToggle = (concept: LumaConceptType) => {
    if (lumaConcepts.includes(concept)) {
      // 既に選択されている場合は削除
      setLumaConcepts(lumaConcepts.filter((item) => item !== concept));
    } else {
      // 選択されていない場合は、選択数が2未満の場合のみ追加
      if (lumaConcepts.length < 2) {
        setLumaConcepts([...lumaConcepts, concept]);
      }
    }
  };

  const isConceptSelected = (concept: LumaConceptType) => {
    return lumaConcepts.includes(concept);
  };

  const getConcepts = (): ConceptMapping[] => {
    return [
      // 基本的なカメラポジション
      { value: "static", label: "Static", video: "Static.mp4" },
      { value: "eye_level", label: "Eye Level", video: "Eye Level Thumbnail.mp4" },

      // 水平移動系
      { value: "truck_left", label: "Truck Left", video: "Truck Left.mp4" },
      { value: "truck_right", label: "Truck Right", video: "Truck Right.mp4" },
      { value: "pan_left", label: "Pan Left", video: "Pan Left ALT.mp4" },
      { value: "pan_right", label: "Pan Right", video: "Pan Right ALT.mp4" },
      { value: "orbit_left", label: "Orbit Left", video: "Orbit Left.mp4" },
      { value: "orbit_right", label: "Orbit Right", video: "Orbit Right.mp4" },

      // 垂直移動系
      { value: "pedestal_up", label: "Pedestal Up", video: "Pedestal Up.mp4" },
      { value: "pedestal_down", label: "Pedestal Down", video: "Pedestal Down.mp4" },
      { value: "crane_up", label: "Crane Up", video: "Crane Up.mp4" },
      { value: "crane_down", label: "Crane Down", video: "Crane Down.mp4" },
      { value: "tilt_up", label: "Tilt Up", video: "Tilt Up.mp4" },
      { value: "tilt_down", label: "Tilt Down", video: "Tilt Down.mp4" },

      // 前後移動・ズーム系
      { value: "push_in", label: "Push In", video: "Push In.mp4" },
      { value: "pull_out", label: "Pull Out", video: "Pull Out.mp4" },
      { value: "zoom_in", label: "Zoom In", video: "Zoom In.mp4" },
      { value: "zoom_out", label: "Zoom Out", video: "Zoom Out.mp4" },
      { value: "dolly_zoom", label: "Dolly Zoom", video: "Dolly Zoom.mp4" },

      // 角度・高さ系
      { value: "low_angle", label: "Low Angle", video: "Low Angle Thumbnail.mp4" },
      { value: "high_angle", label: "High Angle", video: "High Angle Thumbnail.mp4" },
      { value: "ground_level", label: "Ground Level", video: "Ground Level Thumbnail.mp4" },
      { value: "overhead", label: "Overhead", video: "Overhead.mp4" },

      // 回転系
      { value: "roll_left", label: "Roll Left", video: "Roll Left.mp4" },
      { value: "roll_right", label: "Roll Right", video: "Roll Right.mp4" },

      // 視点系
      { value: "pov", label: "POV", video: "First Person View Thumbnail.mp4" },
      { value: "selfie", label: "Selfie", video: "Selfie Thumbnail.mp4" },
      { value: "over_the_shoulder", label: "Over The Shoulder", video: "OTS Thumbnail.mp4" },

      // 特殊効果系
      { value: "handheld", label: "Handheld", video: "Shaky Cam.mp4" },
      { value: "elevator_doors", label: "Elevator Doors", video: "Elevator Doors.mp4" },
      { value: "tiny_planet", label: "Tiny Planet", video: "Tiny Planet.mp4" },
      { value: "bolt_cam", label: "Bolt Cam", video: "Bolt Cam.mp4" },

      // 空撮系
      { value: "aerial", label: "Aerial", video: "Aerial.mp4" },
      { value: "aerial_drone", label: "Aerial Drone", video: "Aerial Drone.mp4" },
    ];
  };

  const getVideoPath = (filename: string): string => {
    // 対応付けテーブルを作成
    const videoMapping = {
      "Aerial Drone.mp4": Videos.juno.lumaCamera.aerialDrone,
      "Aerial.mp4": Videos.juno.lumaCamera.aerial,
      "Bolt Cam.mp4": Videos.juno.lumaCamera.boltCam,
      "Crane Down.mp4": Videos.juno.lumaCamera.craneDown,
      "Crane Up.mp4": Videos.juno.lumaCamera.craneUp,
      "Dolly Zoom.mp4": Videos.juno.lumaCamera.dollyZoom,
      "Elevator Doors.mp4": Videos.juno.lumaCamera.elevatorDoors,
      "Eye Level Thumbnail.mp4": Videos.juno.lumaCamera.eyeLevelThumbnail,
      "First Person View Thumbnail.mp4": Videos.juno.lumaCamera.firstPersonViewThumbnail,
      "Ground Level Thumbnail.mp4": Videos.juno.lumaCamera.groundLevelThumbnail,
      "High Angle Thumbnail.mp4": Videos.juno.lumaCamera.highAngleThumbnail,
      "Low Angle Thumbnail.mp4": Videos.juno.lumaCamera.lowAngleThumbnail,
      "Orbit Left.mp4": Videos.juno.lumaCamera.orbitLeft,
      "Orbit Right.mp4": Videos.juno.lumaCamera.orbitRight,
      "OTS Thumbnail.mp4": Videos.juno.lumaCamera.otsThumbnail,
      "Overhead.mp4": Videos.juno.lumaCamera.overhead,
      "Pan Left ALT.mp4": Videos.juno.lumaCamera.panLeftAlt,
      "Pan Right ALT.mp4": Videos.juno.lumaCamera.panRightAlt,
      "Pedestal Down.mp4": Videos.juno.lumaCamera.pedestalDown,
      "Pedestal Up.mp4": Videos.juno.lumaCamera.pedestalUp,
      "Pull Out.mp4": Videos.juno.lumaCamera.pullOut,
      "Push In.mp4": Videos.juno.lumaCamera.pushIn,
      "Roll Left.mp4": Videos.juno.lumaCamera.rollLeft,
      "Roll Right.mp4": Videos.juno.lumaCamera.rollRight,
      "Selfie Thumbnail.mp4": Videos.juno.lumaCamera.selfieThumbnail,
      "Shaky Cam.mp4": Videos.juno.lumaCamera.shakyCam,
      "Static.mp4": Videos.juno.lumaCamera.static,
      "Tilt Down.mp4": Videos.juno.lumaCamera.tiltDown,
      "Tilt Up.mp4": Videos.juno.lumaCamera.tiltUp,
      "Tiny Planet.mp4": Videos.juno.lumaCamera.tinyPlanet,
      "Truck Left.mp4": Videos.juno.lumaCamera.truckLeft,
      "Truck Right.mp4": Videos.juno.lumaCamera.truckRight,
      "Zoom In.mp4": Videos.juno.lumaCamera.zoomIn,
      "Zoom Out.mp4": Videos.juno.lumaCamera.zoomOut,
    };

    // マッピングテーブルから取得、なければデフォルト値を返す
    return (videoMapping[filename] || Videos.juno.lumaCamera.static) as string;
  };

  // ビデオ再生のハンドラー - 安全に再生を開始する
  const handleVideoPlay = (video: HTMLVideoElement) => {
    if (video) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // エラーが発生した場合、特に何もしない（エラーを無視）
        });
      }
    }
  };

  // ビデオ停止のハンドラー - 安全に停止する
  const handleVideoPause = (video: HTMLVideoElement) => {
    if (video && !video.paused) {
      video.pause();
    }
  };

  return (
    <Box>
      <RowCenteredBox>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}>
          {lumaConcepts.length > 0 ? (
            lumaConcepts.map((concept) => (
              <Chip
                key={concept}
                label={getConcepts().find((c) => c.value === concept)?.label || concept}
                onDelete={() => handleConceptToggle(concept)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary" onClick={handleOpen} sx={{ cursor: "pointer" }}>
              {t("juno.video.cameraMovementPreset.no_concepts_selected")}
            </Typography>
          )}
        </Box>
        <Tooltip title={t("juno.video.cameraMovementPreset.icon_concept")}>
          <IconButton onClick={handleOpen} ref={anchorRef} sx={{ ml: 1 }}>
            <VideocamIcon color="primary" />
          </IconButton>
        </Tooltip>
      </RowCenteredBox>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{}}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
              backdropFilter: "blur(15px)",
              position: "relative",
              width: "700px",
              overflowY: "hidden",
            },
            elevation: 0,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ height: "100%", overflowY: "hide" }}>
            <RowBox
              sx={{ flexDirection: "row", justifyContent: "space-between", display: "flex", alignItems: "flex-end" }}
            >
              <Typography variant="subtitle1">{t("juno.video.cameraMovementPreset.title_concept")}</Typography>
              <Typography variant="caption" color="warning.main" sx={{ mr: 0 }}>
                {t("juno.video.cameraMovementPreset.warning_concept")}
              </Typography>
            </RowBox>
            <ScrollableBox sx={{ height: "380px", mt: 2 }}>
              <Grid container spacing={2}>
                {getConcepts().map((item, index) => (
                  <Grid
                    key={index}
                    size={{
                      xs: 6,
                      sm: 4,
                      md: 3,
                      // lg: 2,
                    }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                        border: isConceptSelected(item.value) ? "2px solid" : "none",
                        borderColor: "primary.main",
                      }}
                      onClick={() => handleConceptToggle(item.value)}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 0,
                          paddingBottom: "56.25%", // 16:9 aspect ratio
                          overflow: "hidden",
                          minWidth: 60,
                        }}
                      >
                        {isConceptSelected(item.value) && (
                          <CheckCircleIcon
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              color: "primary.main",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              borderRadius: "50%",
                              zIndex: 1,
                            }}
                          />
                        )}
                        <Box
                          onMouseEnter={() => {
                            const video = videoRefs.current[item.value];
                            if (video) {
                              video.playbackRate = 2;
                              handleVideoPlay(video);
                            }
                          }}
                          onMouseLeave={() => {
                            const video = videoRefs.current[item.value];
                            if (video) {
                              video.playbackRate = 1;
                              handleVideoPause(video);
                            }
                          }}
                        >
                          <video
                            ref={(el) => (videoRefs.current[item.value] = el)}
                            src={getVideoPath(item.video)}
                            muted
                            loop
                            style={{
                              position: "absolute",
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            preload="metadata"
                          />
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              width: "100%",
                              color: "white",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              py: 0.2,
                            }}
                          >
                            {t(`juno.video.cameraMovementPreset.concept.${item.value}`)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </ScrollableBox>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default LumaConceptsControl;
